import Component from '@glimmer/component'
import { colors } from '@colors'
import { PieChart, type TooltipConfig } from 'teacher-ui-client/components/giraffe/primitives/pie-chart'
import { t } from 'ember-intl'
import type { Point } from 'highcharts'

function tooltipFormatter(this: Point) {
  return `<b>${Math.round(this.y ?? 0)}%</b>`
}

export interface DashboardDrivingTestsDataItem {
  grade_level: number
  quiz_completed_percentage: number
}

interface Args {
  data: DashboardDrivingTestsDataItem[]
  gradeToShortNameMap?: Record<number, string>
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class DashboardDrivingTests extends Component<Signature> {
  get colors() {
    const notCompleted = colors.oceanyBlue300
    const completed = colors.oceanyBlue100
    return [notCompleted, completed]
  }

  get numberOfPiesClass() {
    const numberOfPies = this.args.data.length
    const pieClasses = ['', 'w-full', 'w-1/2', 'w-1/3']

    return pieClasses[numberOfPies]
  }

  get chartInputData() {
    const gradeToShortNameMap = this.args.gradeToShortNameMap ?? {}
    const drivingTestData = this.args.data
    const mapped = drivingTestData.map((drivingTest) => {
      const grade = drivingTest.grade_level
      const shortGradeName = gradeToShortNameMap[grade] ?? '?'
      const completed = drivingTest.quiz_completed_percentage
      const notCompleted = 100 - completed
      const size = this.pieSize
      const inputData = {
        series: [
          {
            data: [
              ['completed', completed],
              ['notCompleted', notCompleted],
            ],
            dataLabels: {
              enabled: false,
            },
            type: 'pie' as const,
          },
        ],
        innerSize: '80%',
        size,
        subtitle: `<h1>${shortGradeName}</h1>`,
        label: completed,
      }
      return inputData
    })
    return mapped
  }

  get pieSize(): string {
    const testsLength = this.args.data.length
    if (testsLength === 1) {
      return '80%'
    }
    return '100%'
  }

  get tooltip(): TooltipConfig {
    return {
      enabled: true,
      backgroundColor: colors.neutral400,
      borderWidth: 0,
      borderRadius: 5,
      formatter: tooltipFormatter,
      shadow: false,
      style: { color: colors.white },
    }
  }

  <template>
    <div data-test-driving-tests class="flex h-full flex-col" ...attributes>
      <div data-test-dashboard-driving-tests-labels class="flex grow items-center">
        {{#each this.chartInputData as |chartData|}}
          <div class="relative top-4 mx-auto mb-2">
            <p
              class="bg-dusty-black-400 text-dusty-black-100 mb-0 flex h-[40px] w-[60px] items-center justify-center rounded-sm"
            >
              {{chartData.label}}
              %
            </p>
            <div
              class="border-y-dusty-black-500 absolute bottom-[-10%] left-[25px] border-x-[5px] border-t-[5px] border-b-0 border-x-transparent"
            ></div>
          </div>
        {{/each}}
      </div>
      <div data-test-dashboard-driving-tests-charts class="relative flex grow-5 flex-row items-center justify-center">
        <div class="absolute inset-0 flex">
          {{! Using display inline block so the below ~ is to avoid whitespace }}
          {{#each this.chartInputData as |chartData index|}}
            <div
              data-test-dashabord-driving-tests-piechart={{index}}
              class="{{this.numberOfPiesClass}} relative inline-block h-full"
            >
              <PieChart
                @data={{chartData.series}}
                @subtitle={{chartData.subtitle}}
                @colors={{this.colors}}
                @size={{chartData.size}}
                @innerSize={{chartData.innerSize}}
                @tooltip={{this.tooltip}}
              />
            </div>
          {{/each}}
        </div>
      </div>
      <div data-test-dashboard-driving-tests-legend class="flex grow items-center justify-around">
        <div data-test-dashboard-driving-tests-legend-item="completed" class="flex flex-row items-center">
          <div
            data-test-dashboard-driving-tests-legend-item-color="completed"
            class="bg-oceany-blue-300 m-1 h-[12px] w-[12px] rounded-full"
          >
          </div>
          <div class="text-dusty-black-500">
            {{t "reporting.charts.dashboard.drivingTests.completed"}}
          </div>
        </div>
        <div data-test-dashboard-driving-tests-legend-item="not-completed" class="flex flex-row items-center">
          <div
            data-test-dashboard-driving-tests-legend-item-color="not-completed"
            class="bg-oceany-blue-200 m-1 h-[12px] w-[12px] rounded-full"
          >
          </div>
          <div class="text-dusty-black-500">
            {{t "reporting.charts.dashboard.drivingTests.notCompleted"}}
          </div>
        </div>
      </div>
    </div>
  </template>
}

export default DashboardDrivingTests
